import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import homeSmartApi from '@/plugins/HomeSmartApi';
import { Company } from '@/modules/backend/Entities/Company';
import { CreateCompanyData } from '@/modules/backend/Dto/CreateCompanyData';
import { EditCompanyData } from '@/modules/backend/Dto/EditCompanyData';

@Module({ store, namespaced: true, name: 'dashboard/companies' })
export class CompaniesModule extends VuexModule {
  private loading = false;
  private items: { [companyId: number]: Company } = {};

  public get Companies(): Company[] {
    return Object.values(this.items).flat();
  }

  public get CompanyById(): (companyId: number) => Company | undefined {
    return (companyId: number): Company | undefined => {
      return this.items[companyId];
    };
  }

  @Action
  public async Create(payload: CreateCompanyData): Promise<Company> {
    const response = await homeSmartApi.Companies.Create(payload);
    this.context.commit('Add', response);
    return response;
  }

  @Action
  public async Fetch(): Promise<Company[]> {
    const response: Company[] = await homeSmartApi.Companies.Fetch();
    this.context.commit('Set', response);
    return response;
  }

  @Action
  public async FetchSingle(companyId: number): Promise<Company | undefined> {
    if (!Number.isInteger(companyId)) {
      return undefined;
    }

    const response: Company | undefined = await homeSmartApi.Companies.GetOne(companyId);
    this.context.commit('Add', response);
    return response;
  }

  @Action
  public async Edit(payload: EditCompanyData): Promise<Company> {
    const response: Company = await homeSmartApi.Companies.Edit(payload);
    this.context.commit('Change', response);
    return response;
  }

  @Action
  public async Remove(company: Company): Promise<void> {
    await homeSmartApi.Companies.Remove(company);
    this.context.commit('Unset', company);
  }

  @Mutation
  private Change(payload: Company): void {
    this.items = Object.assign({}, this.items, {
      [payload.getId()]: payload,
    });
  }

  @Mutation
  private Unset(company: Company): void {
    const companies = { ...this.items };
    delete companies[company.getId()];
    this.items = companies;
  }

  @Mutation
  private Set(payload: Company[]): void {
    const companies = payload.reduce((accum, company: Company) => {
      accum[company.getId()] = company;
      return accum;
    }, {} as {[id: number]: Company});
    this.items = { ...this.items, ...companies };
  }

  @Mutation
  private Add(payload: Company): void {
    this.items = {
      ...this.items,
      [payload.getId()]: payload
    };
  }

  @Mutation
  private setLoading(loading: boolean): void {
    this.loading = loading;
  }
}
